import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

/**
 * The surface variants. `default` is the README card idiom (`bg-card
 * text-card-foreground border-border`); `destructive` follows the
 * destructive-zone pattern — a faint danger surface (`bg-destructive/5`) inside
 * a stronger danger hairline (`border-destructive/35`) — for "delete account"
 * style sections. Tokens only. No `size` axis by design (audit §F2): the card
 * fills its settings column and its interior rhythm is the `--space-card`
 * token, not a per-instance knob.
 */
const formCardVariants = cva('rounded-lg border', {
	variants: {
		variant: {
			default: 'bg-card text-card-foreground border-border',
			destructive:
				'bg-destructive/5 text-card-foreground border-destructive/35',
		},
	},
	defaultVariants: {
		variant: 'default',
	},
})

export type FormCardVariant = NonNullable<
	VariantProps<typeof formCardVariants>['variant']
>

/**
 * FormCard — a bespoke Foundation component (ADR 019): the one card surface that
 * frames both an auth form (header optional, centered) and a settings section
 * (header with title + description). `bg-card text-card-foreground border
 * border-border` at `rounded-lg`, with an optional header (title `text-h6` +
 * optional `text-muted-foreground` description, divided from the body by a
 * border) and padded body.
 *
 * The `destructive` variant applies the destructive-zone pattern (faint danger
 * surface, stronger danger hairline, `text-destructive` title) for "delete
 * account & data" style sections. The title renders as an `<h2>` by default;
 * pass `headingLevel` to place it at the right level for its shell. An optional
 * `action` slot sits opposite the title in the header (e.g. a "Register" button
 * on a list card). Tokens only — no hardcoded colors or sizes.
 */
const FormCard = ({
	title,
	description,
	action,
	variant = 'default',
	headingLevel = 2,
	className,
	children,
	...props
}: React.ComponentProps<'div'> & {
	/** Optional header title (`text-h6`). */
	title?: React.ReactNode
	/** Optional header description under the title. */
	description?: React.ReactNode
	/** Optional trailing header control, aligned opposite the title. */
	action?: React.ReactNode
	/** Surface variant. @default 'default' */
	variant?: FormCardVariant
	/** Heading level for the title. @default 2 */
	headingLevel?: 1 | 2 | 3 | 4 | 5 | 6
}) => {
	const Heading = `h${headingLevel}` as const
	const hasHeader = title != null || description != null || action != null
	return (
		<div
			data-slot="form-card"
			className={cn(formCardVariants({ variant }), className)}
			{...props}
		>
			{hasHeader && (
				<div
					data-slot="form-card-header"
					className="flex items-start justify-between gap-4 border-b border-inherit px-[var(--space-card)] py-3"
				>
					<div className="min-w-0">
						{title != null && (
							<Heading
								className={cn(
									'text-h6',
									variant === 'destructive' && 'text-destructive',
								)}
							>
								{title}
							</Heading>
						)}
						{description != null && (
							<p className="text-muted-foreground text-body-sm mt-1">
								{description}
							</p>
						)}
					</div>
					{action != null && (
						<div data-slot="form-card-action" className="shrink-0">
							{action}
						</div>
					)}
				</div>
			)}
			<div data-slot="form-card-body" className="p-[var(--space-card)]">
				{children}
			</div>
		</div>
	)
}

export { FormCard, formCardVariants }
